import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Page, PageHeader } from '@evogard/ui';
import type { Group } from '@evogard/contract';
import { formatInstant } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { PermissionList } from '../../components/PermissionList.js';
import { can, useSessionContext } from '../../lib/session.js';
import { GroupEditor } from './GroupEditor.js';

export function GroupsScreen() {
  const { data: context } = useSessionContext();
  /*
   * `null` fechado, `undefined` criando, um grupo editando. Três estados num
   * campo só porque são mutuamente exclusivos: dois booleanos permitiriam
   * "criando e editando ao mesmo tempo", que não existe.
   */
  const [editing, setEditing] = useState<Group | undefined | null>(null);

  // esconder não é proteger — quem protege é o guard. Isto evita oferecer o
  // que a API vai recusar, que é o que gera chamado.
  const canEdit = can(context, 'platform.groups', 'editar');

  const query = useQuery({
    queryKey: ['plataforma', 'grupos', context?.currentTenant.id],
    queryFn: async () => {
      const r = await api.platform.listGroups();
      return ok(r, 200, 'Não foi possível carregar os grupos.');
    },
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Grupos de permissão">
        Grupos são por base. Um grupo de {context?.currentTenant.nome} não aparece nas
        outras.
      </PageHeader>

      {canEdit && editing === null && (
        <div>
          <Button onClick={() => setEditing(undefined)}>Novo grupo</Button>
        </div>
      )}

      {editing !== null && (
        <>
          <GroupEditor group={editing} toClose={() => setEditing(null)} />
          {/*
           * Havia um bloco "Premiações que este grupo enxerga" aqui até
           * 16/09/2026. Ele saiu com `sales_award_groups`: a premiação passou a
           * pertencer a um consultor, e quem enxerga o consultor enxerga as
           * premiações dele. Uma segunda régua de visibilidade por cima seria
           * duas respostas para a mesma pergunta, e a mais estreita venceria
           * sem ninguém perceber.
           */}
        </>
      )}

      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar os grupos.">
          Recarregue a página para tentar de novo.
        </Alert>
      )}

      {/* Carregando tinha o mesmo desenho de "não há grupos": nenhum. A tela
          ficava em branco sem dizer se estava buscando ou se acabara de saber
          que não havia nada. */}
      {query.isLoading &&
        [0, 1].map((i) => (
          <Card key={i}>
            <span className="esqueleto block h-4 w-40" />
            <span className="esqueleto mt-3 block h-6 w-full max-w-sm" />
          </Card>
        ))}

      {query.data?.length === 0 && (
        <Card>
          <p className="text-sm text-texto-suave">
            Esta base ainda não tem grupos. Sem grupo, ninguém recebe permissão por
            padrão — só o que uma exceção individual conceder.
          </p>
        </Card>
      )}

      {query.data?.map((g) => (
        <Card
          key={g.id}
          title={g.nome}
          descricao={g.descricao ?? undefined}
          actions={g.archivedAt ? <Badge tone="neutro">Arquivado</Badge> : undefined}
        >
          {/*
           * O aviso vem antes da lista: quem abre a tela precisa saber que
           * aquelas permissões NÃO valem antes de lê-las. Depois da lista, ele
           * chegaria tarde.
           */}
          {g.archivedAt && (
            <Alert tone="alerta" className="mb-3">
              Arquivado em {formatInstant(g.archivedAt)}. Quem está neste grupo{' '}
              <strong className="font-medium">não recebe estas permissões</strong> — elas
              ficam listadas para dizer o que ele concedia.
            </Alert>
          )}

          <PermissionList
            permissions={g.permissions}
            empty="Este grupo não concede nenhuma permissão."
          />
          {canEdit && editing === null && (
            <div className="mt-3 flex gap-2">
              <Button
                variant="contorno"
                size="sm"
                onClick={() => setEditing(g)}
                disabled={Boolean(g.archivedAt)}
              >
                Editar
              </Button>
              <ToggleArchiving group={g} />
            </div>
          )}
        </Card>
      ))}
    </Page>
  );
}

/**
 * Arquivar e reativar.
 *
 * **Arquivar corta o acesso de quem está no grupo, na hora** — `permissoesOf`
 * ignora grupo arquivado e não há cache de permissão. Por isso o botão pergunta
 * antes: é o mesmo padrão de "encerrar sessão" na tela de sessões, e pelo mesmo
 * motivo — diálogo modal para uma linha é preguiça, mas um ato que tira acesso
 * de gente não pode ser um clique só.
 *
 * Reativar não pergunta: devolver acesso não é o ato que precisa de freio.
 */
function ToggleArchiving({ group }: { group: Group }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const archived = Boolean(group.archivedAt);

  const toggle = useMutation({
    mutationFn: async () => {
      const r = await api.platform.archiveGroup({
        params: { id: group.id },
        body: { archived: !archived },
      });
      return ok(r, 200, 'Não foi possível arquivar o grupo.');
    },
    onSuccess: () => {
      setConfirming(false);
      setError(null);
      void client.invalidateQueries({ queryKey: ['plataforma', 'grupos'] });
      /*
       * O contexto também: quem arquiva pode estar no grupo que arquivou, e a
       * interface esconde pelo que o contexto diz. Sem isto, os menus dela
       * continuariam mostrando o que ela acabou de tirar de si mesma.
       */
      void client.invalidateQueries({ queryKey: ['plataforma', 'contexto'] });
    },
    // a tentativa nova começa sem o erro da anterior
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  /*
   * O erro fica AO LADO do controle, nunca no lugar dele.
   *
   * A versão anterior devolvia só a mensagem — `if (erro) return <span…>` — e
   * com isso a primeira falha apagava o botão: sem botão não havia como tentar
   * de novo, e `setErro(null)` só era alcançável a partir de um clique que
   * deixara de existir. Um 500 passageiro tirava a ação da tela até alguém
   * recarregar a página. É o mesmo desenho que `AcoesDaCategoria` já usa.
   */
  const controls = archived ? (
    <Button
      variant="contorno"
      size="sm"
      loading={toggle.isPending}
      onClick={() => toggle.mutate()}
    >
      Reativar
    </Button>
  ) : !confirming ? (
    <Button variant="discreto" size="sm" onClick={() => setConfirming(true)}>
      Arquivar
    </Button>
  ) : (
    <span className="flex items-center gap-1.5">
      <Button
        variant="perigo"
        size="sm"
        loading={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        Arquivar e tirar o acesso
      </Button>
      <Button
        variant="discreto"
        size="sm"
        onClick={() => setConfirming(false)}
        disabled={toggle.isPending}
      >
        Cancelar
      </Button>
    </span>
  );

  if (!error) return controls;

  return (
    <span className="inline-flex flex-col items-end gap-1.5">
      <span role="alert" className="text-xs font-medium text-erro">
        {error}
      </span>
      {controls}
    </span>
  );
}
